import React, { useState } from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';
import {
  AppBuilderProjectFormProps,
  AppBuilderProjectRequest,
  AppBuilderFormErrors,
} from '../types/appbuilder';

const IDEA_LIMIT = 4000;

const EXAMPLE_IDEAS = [
  {
    name: 'midnight-radio',
    surface: 'website',
    label:
      'A desktop shortwave desk you can actually tune — live meters, invented stations, a night log',
    brief:
      'Build a DESKTOP WEBSITE for a computer, not a phone app. Midnight Radio is a wide listening desk: left rail of stations, a large canvas frequency dial you drag, a glowing needle, and three live VU meters driven by Web Audio. Invent 8–10 stations with names, cities, sign-on times, and a late-night show. Clicking a station snaps the dial, changes the bed, and fills the rundown pane. Favourite, write a listener note, keep a week schedule. Mute and a sleep timer that fades out. Persist everything. Use the width — sidebar + stage, hover, keyboard (arrows to tune). First paint is already humming. Do not wrap this in a phone frame.',
  },
  {
    name: 'sky-atlas',
    surface: 'website',
    label:
      'A wide-sky observatory site: click a constellation, log the night, watch the chart drift',
    brief:
      'Build a DESKTOP WEBSITE for a computer, not a phone app. Sky Atlas is a personal observatory page: a large canvas sky (most of the viewport) with drifting stars and constellation lines, plus a side inspector. Click a star or named group for mythology, best months, and a “logged tonight” note. Seed 10 objects (Orion, Vega, the Pleiades, an invented comet, a satellite pass). Journal of nights with search, red-light mode that tints the whole page, zoom. Persist logs. Multi-column, mouse + keyboard. Do not center a 430px phone column.',
  },
  {
    name: 'scent-atelier',
    surface: 'website',
    label:
      'A perfume organ on the desktop: drag notes, watch the accord bloom, bottle formulas',
    brief:
      'Build a DESKTOP WEBSITE for a computer, not a phone app. Scent Atelier is a wide perfume organ: a bench of notes (citrus, flower, wood, smoke, sweet, animalic) you drag onto a blotter. Each drop is a translucent petal on a canvas accord; colour, suggested name, and a three-word character line update live. Nudge top / heart / base. Save, reopen, duplicate, delete bottles. 10 invented notes and 6 seeded formulas (a rainy vetiver, a night-orange cologne). Library search, JSON export/import. Layout is a workbench — rail + stage + inspector. First paint already has a formula open. No phone chrome.',
  },
  {
    name: 'paper-flight',
    surface: 'website',
    label:
      'A nav-desk website: a heading rose that turns, live wind and fuel, a log of hops',
    brief:
      'Build a DESKTOP WEBSITE for a computer, not a phone app. Paper Flight is an analogue E6B on a planner’s desk. A large canvas heading rose you rotate, wind-correction you can feel, and a strip for TAS, wind, variation, distance, and burn. Heading, groundspeed, time en route, and fuel update live. A nav log of 8 seeded hops (dawn coastal, night VFR into grass, a mountain pass) with remarks. Add a leg, mark it flown, reopen that wind. Weight-and-balance stays green or warns red. Split view: instrument + log. It has to calculate. Do not make this a pocket phone app.',
  },
  {
    name: 'type-foundry',
    surface: 'website',
    label:
      'A type foundry on the web: set a specimen, hear the rhythm of the letters, save a face',
    brief:
      'Build a DESKTOP WEBSITE for a computer, not a phone app. Foundry is a specimen studio. A wide page: a live typesetter on the left (size, weight, tracking, leading, optical size) and a specimen cascade on the right — waterfalls, a paragraph, a poster word. Drag sliders; the page reflows immediately. Seed 8 invented faces with stories (a night-train grotesque, a citrus italic). Compare two faces, lock a “house setting”, export the setting as JSON. Keyboard: 1–8 to load a face. This is a designer’s desk in the browser. No mobile tab bar, no phone frame.',
  },
  {
    name: 'corner-gym',
    surface: 'mobile',
    label:
      'A phone boxing trainer: timed rounds, combo pads you tap in rhythm, a fight card',
    brief:
      'Build a MOBILE PHONE APP, thumb-first, about 390–430px wide, with a tab bar. Corner is a boxing gym in your pocket. Tonight’s fight card, a round timer (3 min on / 1 min rest, configurable), and a canvas speed-bag that keeps time. Combo studio: tap jab / cross / hook / slip in order — late flashes, perfect hits thump (Web Audio). Score the round, save the session, 8 seeded workouts with coaches’ notes. History with accuracy. Haptics when available. Persist workouts and prefs. Phone screens: Corner, Combos, Card. Not a desktop dashboard.',
  },
  {
    name: 'lucid-archive',
    surface: 'mobile',
    label:
      'A phone dream archive: stir a hypnagogic canvas, search nights, replay what you saw',
    brief:
      'Build a MOBILE PHONE APP, thumb-first, about 390–430px wide, stacked screens with a back control. Lucid Archive is a dream instrument you open in bed. Stir a hypnagogic canvas, freeze a thumbnail, capture title, lucidity (wake / fog / lucid), tags, a body feeling, and a few concrete lines. Search nights. Replay opens the canvas plus the text. Seed 8 authored nights (a flooded library, a train that only stops if you notice the ticket). A daily reality-check you can mark. Persist dreams and the last canvas seed. Phone only — do not stretch this into a wide website.',
  },
];

const AppBuilderProjectForm: React.FC<AppBuilderProjectFormProps> = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState<AppBuilderProjectRequest>({
    user_name: '',
    project_idea: '',
    project_name: '',
  });
  const [errors, setErrors] = useState<AppBuilderFormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: AppBuilderFormErrors = {};

    if (!formData.user_name.trim()) {
      newErrors.user_name = 'Please enter your name';
    } else if (formData.user_name.length < 2) {
      newErrors.user_name = 'Name must be at least 2 characters long';
    } else if (formData.user_name.length > 50) {
      newErrors.user_name = 'Name must be less than 50 characters';
    }

    if (!formData.project_idea.trim()) {
      newErrors.project_idea = 'Please describe your application';
    } else if (formData.project_idea.length < 10) {
      newErrors.project_idea = 'Please provide at least 10 characters';
    } else if (formData.project_idea.length > IDEA_LIMIT) {
      newErrors.project_idea = `Project idea must be less than ${IDEA_LIMIT} characters`;
    }

    if (formData.project_name && formData.project_name.length > 50) {
      newErrors.project_name = 'Application name must be less than 50 characters';
    }

    if (formData.project_name && !/^[a-zA-Z0-9_-]*$/.test(formData.project_name)) {
      newErrors.project_name =
        'Application name can only contain letters, numbers, hyphens, and underscores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && !isLoading) {
      onSubmit({
        user_name: formData.user_name.trim(),
        project_idea: formData.project_idea.trim(),
        project_name: formData.project_name ? formData.project_name.trim() : undefined,
      });
    }
  };

  const handleInputChange =
    (field: keyof AppBuilderProjectRequest) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const canSubmit = !isLoading && !!formData.user_name.trim() && !!formData.project_idea.trim();

  return (
    <div className="ed-enter">
      <StepHeading
        index={1}
        kicker="App Builder"
        title="Describe it once."
        accent="We'll build and deploy it."
        lede="Claude writes a complete website or a phone app — whichever you asked for — and publishes it to a live URL. No scaffolding, no setup, no code from you."
      />

      <form onSubmit={handleSubmit} className="ed-block ed-block--sheet" style={{ gap: 28 }} autoComplete="off">
        <div className="ed-field">
          <label className="ed-label" htmlFor="user_name">
            Your name
          </label>
          <input
            id="user_name"
            type="text"
            className={`ed-input${errors.user_name ? ' is-invalid' : ''}`}
            {...noAutofill}
            placeholder="Type your name"
            value={formData.user_name}
            onChange={handleInputChange('user_name')}
            disabled={isLoading}
            aria-invalid={!!errors.user_name}
          />
          {errors.user_name && (
            <span className="ed-error" role="alert">
              <AlertCircle size={14} />
              {errors.user_name}
            </span>
          )}
        </div>

        <div className="ed-field">
          <label className="ed-label" htmlFor="project_idea">
            What should it do?
          </label>
          <textarea
            id="project_idea"
            rows={formData.project_idea.length > 180 ? 10 : 4}
            className={`ed-textarea${errors.project_idea ? ' is-invalid' : ''}${formData.project_idea.length > 180 ? ' is-long' : ''}`}
            placeholder="A desktop website for a type foundry, or a phone app for timed boxing rounds…"
            {...noAutofill}
            value={formData.project_idea}
            onChange={handleInputChange('project_idea')}
            disabled={isLoading}
            aria-invalid={!!errors.project_idea}
          />
          {errors.project_idea ? (
            <span className="ed-error" role="alert">
              <AlertCircle size={14} />
              {errors.project_idea}
            </span>
          ) : (
            <span className="ed-hint">
              {formData.project_idea.length} / {IDEA_LIMIT} characters
            </span>
          )}
        </div>

        <div className="ed-field">
          <label className="ed-label" htmlFor="project_name">
            Name it (optional)
          </label>
          <input
            id="project_name"
            type="text"
            className={`ed-input${errors.project_name ? ' is-invalid' : ''}`}
            {...noAutofill}
            placeholder="my-awesome-app"
            value={formData.project_name}
            onChange={handleInputChange('project_name')}
            disabled={isLoading}
            aria-invalid={!!errors.project_name}
          />
          {errors.project_name ? (
            <span className="ed-error" role="alert">
              <AlertCircle size={14} />
              {errors.project_name}
            </span>
          ) : (
            <span className="ed-hint">
              Letters, numbers, hyphens, and underscores. Generated for you if left empty.
            </span>
          )}
        </div>

        <button
          type="submit"
          className="ed-btn ed-btn--primary ed-btn--lg ed-btn--block"
          disabled={!canSubmit}
        >
          {isLoading ? (
            <>
              <span className="ed-spinner" aria-hidden="true" />
              Building…
            </>
          ) : (
            <>
              Build my prototype
              <ArrowRight size={15} />
            </>
          )}
        </button>
      </form>

      {!isLoading && (
        <div className="ed-block">
          <span className="ed-kicker">Or start from one of these</span>
          <div className="ed-ideas">
            {EXAMPLE_IDEAS.map((idea, index) => {
              const selected = formData.project_idea === idea.brief;
              return (
                <button
                  key={idea.name}
                  type="button"
                  className={`ed-idea${selected ? ' is-on' : ''}`}
                  aria-pressed={selected}
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      project_idea: idea.brief,
                      project_name: prev.project_name || idea.name,
                    }));
                    setErrors((prev) => ({ ...prev, project_idea: undefined }));
                    requestAnimationFrame(() => {
                      document.getElementById('project_idea')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    });
                  }}
                >
                  <span className="ed-idea-num">{String(index + 1).padStart(2, '0')}</span>
                  <span className="ed-idea-kind">{idea.surface === 'mobile' ? 'Phone' : 'Website'}</span>
                  <span>{idea.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppBuilderProjectForm;
